<template>
  <div id="solutions-page" class="default-layout-container">
    <div class="content-container-1140">
      <div class="main-content">
        <h1 class="bigger">{{ $t(`navigation.routeTitles.solutions`) }}</h1>
        <div
          class="solutions-intro"
          v-html="md.render(pageContent.data.attributes.SolutionsIntro)"
        ></div>
        <div class="solutions-cat-filter-container">
          <sector-selection-chips
            :init-sector="selectedSector"
            @update:sector="updateUrl"
          ></sector-selection-chips>
        </div>
        <div
          class="solutions-cat-description md-content"
          v-if="selectedSector == 'all'"
          v-html="md.render(pageContent.data.attributes.Summary)"
        ></div>
        <div
          class="solutions-cat-description md-content"
          v-if="selectedSector != 'all'"
          v-html="
            md.render(
              pageContent.data.attributes[selectedSector + 'SectorSummary']
            ) +
            `<a class='read-more' href='` +
            localePath({
              name: 'sector-PageURL',
              params: { PageURL: sectors[selectedSector].slug },
            }) +
            `'>` +
            $t(`solutionLanding.${selectedSector}SectorLinkText`) +
            `</a>`
          "
        ></div>
      </div>
      <div class="solutions-grid-container">
        <div class="solutions-grid-block d-flex">
          <template v-for="(solution, index) in solutions.data" :key="index">
            <div
              class="solutions-grid-item"
              :class="(solution.attributes?.SolutionSector ?? '').toLowerCase()"
              v-if="
                displaySolution(
                  solution.attributes.SolutionTags,
                  solution.attributes?.SolutionSector
                )
              "
              :style="{
                'background-image': `url(${solution.attributes.SolutionImage.data.attributes.formats.medium.url})`,
                'background-size': 'cover',
              }"
            >
              <nuxt-link
                :to="
                  localeRoute({
                    name: 'solutions-SolutionURL',
                    params: { SolutionURL: solution.attributes.SolutionURL },
                  }).fullPath
                "
                external
              >
                <v-icon
                  :icon="`$${sectors[solution.attributes.SolutionSector].icon}`"
                ></v-icon>
                <div class="solutions-grid-item-title">
                  {{ solution.attributes.SolutionTitle }}
                </div>
              </nuxt-link>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
const selectedSector = ref("all");
const { sectors, sectorLocaleXRefs }: any = usePathwaysConfig();
const selectedTags = ref([]);
const { locale, t } = useI18n();
const { md } = useMarkdownIt();
const localeRoute = useLocaleRoute();
const route = useRoute();
const router = useRouter();

const displaySolution = (tagsRaw, sectorRaw) => {
  let tagsCondition = true;
  let sectorCondition = true;

  if (selectedTags.value.length === 0) {
    // no tags selected
    tagsCondition = true;
  } else if (tagsRaw === null || tagsRaw === undefined) {
    // tags selected, but solution has no tags
    tagsCondition = false;
  } else {
    const tags = tagsRaw
      .trim()
      .split(",")
      .map((tag) => tag.trim().toLowerCase().replace(/ /g, ""));
    tagsCondition =
      selectedTags.value.length === 0 ||
      tags.some((tag) => selectedTags.value.includes(tag));
  }

  const sector = sectorRaw.toLowerCase(); // solution always has a sector
  sectorCondition =
    selectedSector.value === "all" || selectedSector.value === sector;

  return tagsCondition && sectorCondition;
};

const updateUrl = (sector) => {
  // todo - account for tags if/when they are implemented
  selectedSector.value = sector;
  const newUrl =
    sector === "all"
      ? localeRoute({ name: "solutions_overview-sector" })
      : localeRoute({
          name: "solutions_overview-sector",
          params: { sector: sectors[sector].slug },
        });
  router.replace(newUrl.fullPath);
};

onBeforeMount(() => {
  if (route.params.sector !== "") {
    selectedSector.value = sectorLocaleXRefs[route.params.sector];
  }

  const headTitle: string = selectedSector.value !== 'all' ? `${t("solutions.exploreSolutions")} - ${t("explorer.sectors." + selectedSector.value + ".name")}` : t("solutions.exploreSolutions")
  useHead({
    title: headTitle
  });
});

// api content
const contentType = ["solutions-landing", "solutions"];
const key = [
  `${contentType[0]}_${locale.value}`,
  `${contentType[1]}_landing_${locale.value}`,
];

const apiURL = [
  getStrapiLink(contentType[0], {
    fields: [
      "SolutionsIntro",
      "Summary",
      "energySectorSummary",
      "agricultureSectorSummary",
      "wasteSectorSummary",
    ],
  }),
  getStrapiLink(contentType[1], {
    fields: ["SolutionSector", "SolutionURL", "SolutionTitle", "SolutionTags"],
    populate: ["SolutionImage"],
  }),
];

// when multiple useFetch calls are required, they need to be put inside a Promise.all() (see https://github.com/nuxt/nuxt/issues/14725#issuecomment-1397364309 example)
const [{ data: pageContent }, { data: solutions }] = await Promise.all([
  useFetch(apiURL[0], {
    key: key[0],
    watch: false,
  }),
  useFetch(apiURL[1], {
    key: key[1],
    watch: false,
  }),
]);
</script>

<style lang="scss" scoped>
.solutions-intro {
  font-size: 24px;
  font-weight: 500;
}
.solutions-intro :deep(p) {
  margin-bottom: 1em;
}
.solutions-intro :deep(p:last-child) {
  margin-bottom: 0;
}
// Grid Style
.solutions-grid-container {
  padding-bottom: 100px;

  @media (max-width: 768px) {
    padding-bottom: 50px;
  }
}
.solutions-grid-block {
  width: 100%;
  flex-wrap: wrap;
  margin-left: -15px;
  margin-right: -15px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
}
.solutions-grid-item {
  width: calc(25% - 30px);
  margin: 15px;
  position: relative;
  aspect-ratio: 1.18;

  @media (max-width: 1023px) {
    width: calc(50% - 30px);
  }

  @media (max-width: 700px) {
    width: 100%;
  }

  .v-icon {
    height: 40px;
    width: 40px;
    position: absolute;
    top: 0;
    left: 0;
    color: #050505;
    font-size: 20px;
  }

  :deep(.svg-inline--fa) {
    width: 24px;
    height: 24px;
  }

  .all-solutions-link,
  .solutions-grid-item-title {
    font-size: 21px;
    line-height: 1.19em;
    font-weight: 700;
  }

  .solutions-grid-item-title {
    position: absolute;
    top: 0px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center; /* for vertical alignment */
    justify-content: center;
    text-align: center;
    color: #fff;
    padding: 20px;
  }
  .all-solutions-link {
    color: #247775;
    text-decoration: none;
  }
  .all-solutions-link:hover {
    text-decoration: underline;
  }
}
.solutions-grid-item::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(
    0,
    0,
    0,
    0.4
  ); /* Adjust the 0.5 to control the level of darkening */
  z-index: 0;
  transition: background-color 0.3s ease-in-out;
}
.solutions-grid-item:hover::before {
  background: rgba(
    0,
    0,
    0,
    0.6
  ); /* Adjust the 0.5 to control the level of darkening */
}

// Color Variations
.solutions-grid-item.agriculture {
  .v-icon {
    background-color: #33a28f;
  }
}
.solutions-grid-item.energy {
  .v-icon {
    background-color: #f1d76a;
  }
}
.solutions-grid-item.waste {
  .v-icon {
    background-color: #cb5546;
  }
}

// Filter Styles
// Category Styles
.solutions-cat-filter-block {
  display: flex;
  flex-wrap: wrap;
  margin-top: 80px;
  margin-bottom: 20px;

  @media (max-width: 449px) {
    flex-wrap: nowrap;
    flex-direction: column;
    align-items: center;
  }
}
.solutions-cat-filter-item:not(:last-child) {
  margin-right: 50px;
  @media (max-width: 449px) {
    margin-right: 0;
  }
}
.solutions-cat-filter-item {
  font-size: 20px;
  font-weight: 500;
  color: #247775;
  text-decoration: none;
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 0 5px 0 !important;
  overflow: unset;
  background-color: transparent !important;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  margin-bottom: 25px;

  @media (max-width: 649px) {
    width: calc(50% - 50px);
  }
  @media (max-width: 449px) {
    width: fit-content;
    text-align: center;
  }
  &.v-chip--selected {
    color: #050505;
    border-color: #247775;

    .v-chip__overlay {
      display: none !important;
    }
  }
}

.v-chip--variant-text .v-chip__overlay {
  display: none !important;
  background-color: transparent !important;
  opacity: 0 !important;
  width: 0 !important;
}

// Tag Styles
.solutions-tag-filter-container {
  display: flex;
  margin-bottom: 20px;

  @media (max-width: 500px) {
    flex-wrap: wrap;
  }

  .v-icon {
    font-size: 20px !important;
  }
}
.tag-filter-by-title {
  font-weight: 500;
  margin-right: 25px;
  margin-bottom: 40px;
  width: 70px;
}

.solutions-tag-filter-block {
  width: calc(100% - 70px);
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  padding: 0;

  @media (max-width: 500px) {
    width: 100%;
  }
}
.solutions-tag-filter-item {
  margin-top: 0;
  margin-right: 20px;
  margin-bottom: 20px;
  border: 1px solid #247775;
  color: #247775;
  font-weight: 500;
  text-decoration: none;
  padding: 5px 20px;
  border-radius: 20px;

  &:hover,
  &.v-chip--selected {
    color: #ffffff;
    background-color: #247775;
  }
}

// Solutions description
.solutions-cat-description {
  font-size: 21px;
  margin-bottom: 55px;
  :deep(p:first-child) {
    font-weight: normal;
    font-size: 21px;
  }
}
</style>
